/**
 * Which calculation type survives a country change, and whether the user needs telling.
 *
 * Calculation types are per-country. The wizard's country follows the address (`Address.vue`'s watcher),
 * so entering an address in another country refetches the list, and a type chosen for the old country is
 * simply not in the new one. The step then fell back to Custom silently — which also wipes the included
 * phases, and with them the Transport, Installation and Energy use steps. The user chose BR18 (2025) and
 * ended up on Custom with nothing assessed, unprompted.
 *
 * Pure so the "was anything actually lost" question is a unit test rather than a manual click-through.
 */

/** The shape this needs from a calculation type. */
export type SelectableType = { id: number; label: string };

export type CalculationTypeResolution<TType extends SelectableType> = {
  /** The type to use for the new country. Undefined when the list has no Custom to fall back to. */
  next: TType | undefined;
  /**
   * True only when a real previous choice could not be honoured. False when nothing had been chosen yet,
   * so ordinary first loads stay quiet.
   */
  wasInvalidated: boolean;
  /** The label that was lost, for the message. Null unless wasInvalidated. */
  lostLabel: string | null;
};

/**
 * `currentId` of 0 means "no choice yet", not "Custom was chosen".
 *
 * The project template used to seed `calculationTypeEnum: 0`, and 0 is also Custom's id in Hasura for
 * Denmark. The template seeds null now (RTLCA-5595), but projects saved while it seeded 0 — and every
 * Danish project that genuinely chose Custom — still arrive here as 0, so from `currentId` alone
 * "chose Custom" and "chose nothing" remain the same number for the old population.
 *
 * DO NOT "fix" this to `currentId !== null && currentId !== undefined`, which is the obvious change and is
 * wrong twice over. Reviewed and measured:
 *
 *  1. The caller's watcher fires on the `[] -> [rows]` transition, i.e. on every new project's FIRST load
 *     in every country. For any country whose Custom is not id 0 (Sweden's is 20) the lookup misses on
 *     that first load, so the "not available, switched to Custom" toast would fire on every new Swedish,
 *     Icelandic and Finnish project the moment it opens. That is the regression this trade avoids.
 *  2. It breaks the existing "stays quiet on an empty list with nothing chosen" case deliberately, not
 *     incidentally — see the test of that name.
 *
 * The accepted cost is one missed toast: a user who picks Denmark + Custom and then changes country is
 * moved onto the new country's Custom without being told. Low consequence, because the fallback lands on
 * the same label and the same hand-pick-your-phases behaviour, so nothing about the editing experience
 * changes. Closing it properly needs a `hasUserChosenCalculationType` signal threaded in from outside,
 * which is a design change rather than a one-line fix.
 */
export const resolveCalculationTypeForCountry = <TType extends SelectableType>(
  availableTypes: TType[],
  currentId: number | null | undefined,
  currentLabel?: string | null
): CalculationTypeResolution<TType> => {
  const matched =
    currentId !== null && currentId !== undefined ? availableTypes.find((type) => type.id === currentId) : undefined;

  if (matched) return { next: matched, wasInvalidated: false, lostLabel: null };

  const custom = availableTypes.find((type) => type.label === "Custom");
  const hadRealChoice = !!currentId; // 0, null and undefined all mean "nothing chosen" — see above

  return {
    next: custom,
    wasInvalidated: hadRealChoice,
    lostLabel: hadRealChoice ? currentLabel ?? null : null,
  };
};

/**
 * Whether a calculation-type list is the PROJECT'S list, and so allowed to invalidate its choice.
 *
 * The resolver above answers "is the current type in this list" — it cannot know whether the list it
 * was handed is worth asking. The provider's list follows the LOCALIZATION country, which follows the
 * project's address, and on page entry those move at different times: the caller's watcher fires
 * `immediate` with whatever the provider still holds, which is empty on a cold app, and the PREVIOUS
 * project's list when hopping between projects of different countries. A Danish project's BR18 is not
 * in Norway's list — indistinguishable, from the resolver's seat, from a real invalidation. It toasted
 * "not available for this country. Switched to Custom", pointed the form at the WRONG country's Custom,
 * and re-resolved thresholds against it, all before the project's own list had arrived (observed on the
 * settings page, 2026-08-28, intermittently — it depends on which project the provider served last).
 *
 * A list is authoritative exactly when it is non-empty and every row that declares a country declares
 * the project's. Rows without a country (stories, fixtures) do not veto — a list that says nothing
 * about countries cannot be from the wrong one. No project country means NO list is authoritative:
 * a choice cannot be invalidated against a country the project does not have.
 */
export const isCalculationTypeListAuthoritative = (
  availableTypes: ReadonlyArray<{ country?: string | null }>,
  projectCountry: string | null | undefined
): boolean => {
  if (!projectCountry || availableTypes.length === 0) return false;
  return availableTypes.every((type) => !type.country || type.country === projectCountry);
};
